import { PatientTreatmentPlanStageLog } from '../../../../../../types/healthcare/TreatmentPlanTypes';

/** View-model for one action an action-stage actually performed. */
export interface PerformedActionVM {
  key: string;
  label: string;
  name?: string;
  status: string;
  reason?: string;
}

export const humanizeAction = (s: string): string =>
  String(s ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ACTION_LABELS: Record<string, string> = {
  assign_form: 'Form assigned to patient',
  send_notification: 'Notification sent to patient',
  assign_global_resource: 'Resource shared with patient',
  assign_medication: 'Medication assigned',
  cancel_appointment: 'Appointment cancelled',
  dispatch_consult_job: 'Consultation guidance generated',
  request_script_upload: 'Script upload requested',
  send_booking_link: 'Booking link sent to patient',
};

/**
 * Build a human-readable list of the actions an action-stage actually performed, pairing each
 * runtime `actions_performed` entry (status: assigned / sent / skipped …) with the configured
 * action that carries the friendly name (form/template/resource name). Trigger evaluations are
 * excluded. Used by the action view so the practitioner sees exactly what happened.
 */
export function buildPerformedActions(log: PatientTreatmentPlanStageLog): PerformedActionVM[] {
  const performed = (log.actions_performed ?? []) as Record<string, any>[];
  const cfgActions = (log.stage_snapshot?.configuration?.actions
    ?? log.stage?.configuration?.actions
    ?? []) as Record<string, any>[];

  return performed
    .filter((p) => p.action && p.action !== 'trigger_evaluated')
    .map((p, i) => {
      const cfg =
        (cfgActions[i]?.action_type === p.action
          ? cfgActions[i]
          : cfgActions.find((c) => c.action_type === p.action))?.config ?? {};
      const name =
        cfg.form_name ?? cfg.template_name ?? cfg.resource_name ?? cfg.medication_name ?? cfg.appointment_type_name;
      return {
        key: `${p.action}_${i}`,
        label: ACTION_LABELS[p.action] ?? humanizeAction(p.action),
        name,
        status: String(p.status ?? 'done'),
        reason: p.reason,
      };
    });
}
